import { useTranslation } from "react-i18next";
import { Clock, Folder, Heart, LayoutGrid, UploadCloud, type LucideIcon } from "lucide-react";
import { useLibraryFolders } from "@/api/hooks/use-library-folders";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { cn } from "@/lib/utils";
import { useFilePickerNavigation, usePickerRecentItems } from "../store/file-picker-store";
import type { PickerSource } from "../types/file-picker-types";

interface NavEntry {
  id: string;
  source: PickerSource;
  folderId?: string;
  label: string;
  icon: LucideIcon;
  count?: number;
}

interface PickerSourcesNavProps {
  layout: "sidebar" | "chips";
}

const GoogleDriveMark = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M7.7 3.5h8.6l5.2 9-4.3 7.5H6.8L2.5 12.5l5.2-9Zm1.2 2L5 12.5l2.9 5h8.2l2.9-5-3.9-7H8.9Z" />
  </svg>
);

/** Seçicinin kaynak menüsü: masaüstünde sol sütun, telefonda yatay kayan çipler. */
export const PickerSourcesNav = ({ layout }: PickerSourcesNavProps) => {
  const { t } = useTranslation();
  const { source, folderId, setSource } = useFilePickerNavigation();
  const recentItems = usePickerRecentItems();
  const { data: folders = [] } = useLibraryFolders();
  const isSidebar = layout === "sidebar";

  const mainEntries: NavEntry[] = [
    { id: "upload", source: "upload", label: t("filePicker.sources.upload"), icon: UploadCloud },
    { id: "all", source: "all", label: t("filePicker.sources.all"), icon: LayoutGrid },
    {
      id: "recent",
      source: "recent",
      label: t("filePicker.sources.recent"),
      icon: Clock,
      count: recentItems.length || undefined,
    },
    { id: "favorites", source: "favorites", label: t("filePicker.sources.favorites"), icon: Heart },
  ];
  const folderEntries: NavEntry[] = folders.map((folder) => ({
    id: `folder-${folder._id}`,
    source: "folder",
    folderId: folder._id,
    label: folder.name,
    icon: Folder,
    count: folder.itemCount,
  }));

  const renderEntry = (entry: NavEntry) => {
    const isActive =
      entry.source === source && (entry.source !== "folder" || entry.folderId === folderId);
    const Icon = entry.icon;
    return (
      <button
        key={entry.id}
        type="button"
        onClick={() => setSource(entry.source, entry.folderId)}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          libraryGlowButtonClass,
          "flex items-center gap-2.5 text-sm font-medium transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60",
          isSidebar ? "w-full h-10 px-3 rounded-xl" : "shrink-0 h-9 px-3.5 rounded-full",
          isActive
            ? "bg-black/[0.06] dark:bg-white/[0.09] text-text-primary dark:text-white"
            : cn(
                "text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-white",
                !isSidebar && "ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08]"
              )
        )}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className={cn(isSidebar && "flex-1 min-w-0 truncate text-left")}>{entry.label}</span>
        {entry.count !== undefined && (
          <span className="text-xs tabular-nums text-text-quaternary dark:text-gray-500">
            {entry.count}
          </span>
        )}
      </button>
    );
  };

  const driveEntry = (
    <div
      aria-disabled
      title={t("filePicker.googleDriveInDevelopment")}
      className={cn(
        "flex items-center gap-2.5 text-sm font-medium text-text-quaternary dark:text-gray-600 cursor-not-allowed whitespace-nowrap",
        isSidebar ? "w-full h-10 px-3" : "shrink-0 h-9 px-3.5 rounded-full ring-1 ring-inset ring-black/[0.04] dark:ring-white/[0.05]"
      )}
    >
      <GoogleDriveMark className="w-4 h-4 shrink-0" />
      <span className={cn(isSidebar && "flex-1 min-w-0 truncate text-left")}>
        {t("filePicker.tabs.googleDrive")}
      </span>
      <span className="rounded-full bg-black/[0.05] dark:bg-white/[0.07] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary dark:text-gray-400">
        {t("filePicker.comingSoon")}
      </span>
    </div>
  );

  if (!isSidebar) {
    return (
      <nav
        aria-label={t("filePicker.sources.label")}
        className="flex items-center gap-2 overflow-x-auto overscroll-x-contain px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {mainEntries.map(renderEntry)}
        {folderEntries.map(renderEntry)}
        {driveEntry}
      </nav>
    );
  }

  return (
    <nav
      aria-label={t("filePicker.sources.label")}
      className="flex h-full flex-col gap-1 overflow-y-auto p-3"
    >
      {mainEntries.map(renderEntry)}

      {folderEntries.length > 0 && (
        <>
          <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-text-quaternary dark:text-gray-500">
            {t("filePicker.sources.folders")}
          </p>
          {folderEntries.map(renderEntry)}
        </>
      )}

      <div className="mt-auto pt-4">{driveEntry}</div>
    </nav>
  );
};
